import { useState } from "react";
import { useGroupSessions } from "@/queries/group-visit/use-group-sessions";
import { DEF_PAGINATION, DGT_Pagination } from "@/utils/utils";
import {
  GROUP_SESSION_DEFAULT_SORT_BY,
  GROUP_SESSION_DEFAULT_SORT_DIRECTION,
} from "../group-appointment-constant";
import { GroupAppointment } from "../group-appointment-types";

export type UseGroupAppointmentList = {
  appointments: GroupAppointment[];
  totalCount: number;
  isLoading: boolean;
  pagination: DGT_Pagination;
  setPagination: (value: DGT_Pagination) => void;
};

type Params = {
  /** [from, to] ISO range from the Appointments date navigation. */
  filterDate: [string, string];
  /** Group type and therapy category the list is narrowed by. */
  groupType?: string;
  therapyCategory?: string;
  /** Providers and patient selected in the side filters. */
  providerIds?: string[];
  patientId?: string;
  /** Location and status checkboxes ticked in the side filters. */
  locationIds: string[];
  sessionStatuses: string[];
};

/**
 * Group sessions for the selected date range, oldest first.
 *
 * @author Rajkumar Rathod
 */
export const useGroupAppointmentList = ({
  filterDate,
  groupType,
  therapyCategory,
  providerIds,
  patientId,
  locationIds,
  sessionStatuses,
}: Params): UseGroupAppointmentList => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [fromDate, toDate] = filterDate;

  const { data, isLoading, isFetching } = useGroupSessions({
    pagination,
    fromDate,
    toDate,
    groupType: groupType || undefined,
    therapyCategory: therapyCategory || undefined,
    providerIds,
    patientId: patientId || undefined,
    locationIds,
    sessionStatuses,
    sortBy: GROUP_SESSION_DEFAULT_SORT_BY,
    sortDirection: GROUP_SESSION_DEFAULT_SORT_DIRECTION,
    enabled: !!fromDate && !!toDate,
  });

  return {
    appointments: (data?.data?.content ?? []) as GroupAppointment[],
    totalCount: data?.data?.page?.totalElements ?? 0,
    isLoading: isLoading || isFetching,
    pagination,
    setPagination,
  };
};
